import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface AppRow {
  readonly applicationId: string;
  readonly appCode: string;
  readonly appName: string;
  readonly appType: string;
  readonly lifecycleStatus: string;
  readonly currentEnvironment: string;
  readonly currentVersionNo: number;
  readonly publishScope: string;
}

const QUICK_LINKS: ReadonlyArray<readonly [string, string, string, string]> = [
  ['/admin/app-platform/applications', 'Application Builder', 'm40.studio.link.appBuilder.label', 'm40.studio.link.appBuilder.desc'],
  ['/admin/app-platform/forms', 'Form Designer', 'm40.studio.link.formDesigner.label', 'm40.studio.link.formDesigner.desc'],
  ['/admin/app-platform/workflows', 'Workflow Designer', 'm40.studio.link.workflowDesigner.label', 'm40.studio.link.workflowDesigner.desc'],
  ['/admin/app-platform/rules', 'Business Rule Designer', 'm40.studio.link.ruleDesigner.label', 'm40.studio.link.ruleDesigner.desc'],
  ['/admin/app-platform/dashboards', 'Dashboard Builder', 'm40.studio.link.dashBuilder.label', 'm40.studio.link.dashBuilder.desc'],
  ['/admin/app-platform/reports', 'Report Builder', 'm40.studio.link.reportBuilder.label', 'm40.studio.link.reportBuilder.desc'],
  ['/admin/app-platform/deployments', 'Deployment Console', 'm40.studio.link.deployment.label', 'm40.studio.link.deployment.desc'],
  ['/admin/app-platform/versions', 'Version Management', 'm40.studio.link.version.label', 'm40.studio.link.version.desc'],
  ['/admin/app-platform/governance', 'Governance', 'm40.studio.link.governance.label', 'm40.studio.link.governance.desc'],
  ['/admin/app-platform/audit', 'Audit Trail', 'm40.studio.link.audit.label', 'm40.studio.link.audit.desc'],
];

/**
 * M40 — Low-Code Studio (§1/§28). The citizen-developer landing surface over the
 * enterprise LCNC platform. Summarises the tenant's applications by lifecycle
 * and links out to every builder / console. Viewing needs STUDIO_VIEW; tenant
 * isolation is enforced server-side (LAW 4).
 */
export function LowCodeStudio(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: AppRow[]; total: number }>(`/v1/app-platform/applications?pageSize=100`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const countBy = (predicate: (row: AppRow) => boolean): number => rows.filter(predicate).length;
  const published = countBy((r) => r.lifecycleStatus === 'PUBLISHED');
  const draft = countBy((r) => r.lifecycleStatus === 'DRAFT');
  const inProd = countBy((r) => r.currentEnvironment === 'PROD');

  return (
    <main aria-label="low-code-studio">
      <h1>{t('m40.studio.title')}</h1>
      <p>{t('m40.studio.subtitle')}</p>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <section aria-label="studio-summary">
            <h2>{t('m40.studio.platformOverview')}</h2>
            <ul>
              <li aria-label="summary-total">{t('m40.studio.totalApps', { value: total })}</li>
              <li aria-label="summary-published">{t('m40.studio.published', { value: published })}</li>
              <li aria-label="summary-draft">{t('m40.studio.draft', { value: draft })}</li>
              <li aria-label="summary-prod">{t('m40.studio.liveProd', { value: inProd })}</li>
            </ul>
          </section>

          <section aria-label="studio-links">
            <h2>{t('m40.studio.buildersConsoles')}</h2>
            <ul>
              {QUICK_LINKS.map(([to, enLabel, labelKey, descKey]) => (
                <li key={to}>
                  <Link to={to} aria-label={`link-${enLabel}`}>{t(labelKey)}</Link>
                  <span> — {t(descKey)}</span>
                </li>
              ))}
            </ul>
          </section>

          <section aria-label="studio-recent">
            <h2>{t('m40.studio.recentApps')}</h2>
            <DataTable<AppRow>
              ariaLabel="studio-app-table"
              testId="studio-app-table"
              rowKey={(r) => r.applicationId}
              rowDataAttrs={(r) => ({ 'data-app': r.appCode })}
              emptyMessage={t('m40.studio.empty')}
              rows={rows.slice(0, 10)}
              columns={[
                { key: 'code', header: t('m40.common.code'), render: (r) => r.appCode },
                { key: 'name', header: t('m40.common.name'), render: (r) => r.appName },
                { key: 'type', header: t('m40.common.type'), render: (r) => r.appType },
                { key: 'lifecycle', header: t('m40.common.lifecycle'), cellAriaLabel: (r) => `lifecycle-${r.appCode}`, render: (r) => r.lifecycleStatus },
                { key: 'environment', header: t('m40.common.environment'), cellAriaLabel: (r) => `env-${r.appCode}`, render: (r) => r.currentEnvironment },
                { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.currentVersionNo}` },
              ]}
            />
          </section>
        </>
      )}
    </main>
  );
}
